import React from "react";
import { Box, Collapse, Typography } from "@mui/material";
import { ChevronBigDown, ChevronBigUp } from "@tiny/icons";
import useId from "../../hooks/useId";
import { EmailPreviewDetailsProps } from "./EmailPreviewDetails.types";
import { buildSenderLine } from "./EmailPreviewDetails.helpers";

/**
 * Details shown above a workflow email preview so the subject, preview text and
 * sender can be checked without going back to the Details step (MAR-12116).
 * Expanded by default; collapsible to get it out of the way.
 *
 * Everything here comes from props the workflow builder has already loaded, so
 * this costs no request at all.
 */
function EmailPreviewDetails(props: EmailPreviewDetailsProps) {
  const { subject, previewText, senderName, senderEmail } = props;
  const [expanded, setExpanded] = React.useState(true);
  const detailsId = useId();

  const senderLine = buildSenderLine(senderName, senderEmail);

  const rows = [
    { label: "Subject", value: subject },
    { label: "Preview Text", value: previewText },
    // The sender may have been deleted since the email was set up.
    ...(senderLine ? [{ label: "Sender", value: senderLine }] : []),
  ];

  // Template-only previews have none of this; keep their layout untouched.
  if (!subject && !previewText && !senderLine) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: "16px",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: "grey.300",
        bgcolor: "common.white",
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        aria-controls={detailsId}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          p: "10px 14px",
          background: "none",
          border: 0,
          font: "inherit",
          textAlign: "left",
          color: "grey.700",
          cursor: "pointer",
          "&:hover": { color: "grey.900" },
        }}
      >
        <Typography variant="label2" color="inherit">
          Email details
        </Typography>
        <Box
          component="span"
          sx={{ display: "flex", flexShrink: 0, ml: "8px", color: "grey.600" }}
        >
          {expanded ? <ChevronBigUp /> : <ChevronBigDown />}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box
          component="dl"
          id={detailsId}
          sx={{
            m: 0,
            p: "12px 14px",
            borderTop: "1px solid",
            borderColor: "grey.300",
          }}
        >
          {rows.map(({ label, value }, index) => (
            <Box key={label} sx={{ mt: index === 0 ? 0 : "10px" }}>
              <Typography
                component="dt"
                color="grey.600"
                variant="label2"
                sx={{
                  display: "block",
                  mb: "2px",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {label}
              </Typography>
              <Typography
                component="dd"
                color={value ? "grey.900" : "grey.600"}
                variant="label1R"
                sx={{ display: "block", m: 0, overflowWrap: "anywhere" }}
              >
                {value || "Not set"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default React.memo(EmailPreviewDetails);
